import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { ChevronLeft, Eye } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/hooks/use-auth";
import { supabase, type JewelryItem, type ClothingItem } from "@/lib/supabase";
import { AppShell } from "@/components/app-shell";
import { ImageBox } from "@/components/ui-extras";
import { EmptyState } from "@/components/empty-state";
import { cleanItemName } from "@/lib/style-matching";
import { BG_PREFERENCE_SELECT, bgFields, resolvePreferredPhoto } from "@/lib/photo-preference";

export const Route = createFileRoute("/your-edit")({
  head: () => ({
    meta: [
      { title: "Your Edit — Cailève" },
      { name: "description", content: "Pieces you are still deciding on." },
    ],
  }),
  component: () => (
    <AppShell>
      <YourEdit />
    </AppShell>
  ),
});

type EditItem = {
  id: string;
  name: string;
  photo_url: string | null;
  kind: "jewelry" | "clothing" | "accessory";
  created_at: string;
  eye_verdict: string | null;
  photo_url_no_bg: string | null;
  prefer_clean_bg: boolean | null;
  clean_bg_color: string | null;
};

function YourEdit() {
  const { user } = useAuth();
  const uid = user?.id ?? null;
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<"all" | "jewelry" | "clothing" | "accessory">("all");

  const { data: jewelryEdit, isLoading: loadingJ } = useQuery({
    queryKey: ["jewelry_wishlist", uid],
    enabled: !!uid,
    staleTime: 2 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("jewelry_items")
        .select(`id, name, photo_url, is_accessory, created_at, eye_verdict, ${BG_PREFERENCE_SELECT}`)
        .eq("user_id", uid!)
        .eq("is_wishlist", true)
        .eq("is_accessory", false)
        .order("created_at", { ascending: false });
      if (error) throw new Error(error.message || "Network error");
      return (data ?? []) as Array<JewelryItem & { is_accessory: boolean }>;
    },
  });

  const { data: clothingEdit, isLoading: loadingC } = useQuery({
    queryKey: ["clothing_wishlist", uid],
    enabled: !!uid,
    staleTime: 2 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("clothing_items")
        .select(`id, name, photo_url, created_at, eye_verdict, ${BG_PREFERENCE_SELECT}`)
        .eq("user_id", uid!)
        .eq("is_wishlist", true)
        .order("created_at", { ascending: false });
      if (error) throw new Error(error.message || "Network error");
      return (data ?? []) as ClothingItem[];
    },
  });

  const { data: accessoryEdit, isLoading: loadingA } = useQuery({
    queryKey: ["accessory_wishlist", uid],
    enabled: !!uid,
    staleTime: 2 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("jewelry_items")
        .select(`id, name, photo_url, is_accessory, created_at, eye_verdict, ${BG_PREFERENCE_SELECT}`)
        .eq("user_id", uid!)
        .eq("is_wishlist", true)
        .eq("is_accessory", true)
        .order("created_at", { ascending: false });
      if (error) throw new Error(error.message || "Network error");
      return (data ?? []) as Array<JewelryItem & { is_accessory: boolean }>;
    },
  });

  const promoteJewelry = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("jewelry_items")
        .update({ is_wishlist: false } as never)
        .eq("id", id);
      if (error) throw new Error(error.message || "Network error");
    },
    onSuccess: () => {
      toast.success("Added to your collection.");
      qc.invalidateQueries({ queryKey: ["jewelry_wishlist", uid] });
      qc.invalidateQueries({ queryKey: ["jewelry_items", uid] });
      qc.invalidateQueries({ queryKey: ["recently_considered", uid] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const promoteClothing = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("clothing_items")
        .update({ is_wishlist: false } as never)
        .eq("id", id);
      if (error) throw new Error(error.message || "Network error");
    },
    onSuccess: () => {
      toast.success("Added to your collection.");
      qc.invalidateQueries({ queryKey: ["clothing_wishlist", uid] });
      qc.invalidateQueries({ queryKey: ["clothing_items", uid] });
      qc.invalidateQueries({ queryKey: ["recently_considered", uid] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const promoteAccessory = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("jewelry_items")
        .update({ is_wishlist: false } as never)
        .eq("id", id);
      if (error) throw new Error(error.message || "Network error");
    },
    onSuccess: () => {
      toast.success("Added to your collection.");
      qc.invalidateQueries({ queryKey: ["accessory_wishlist", uid] });
      qc.invalidateQueries({ queryKey: ["accessory_items", uid] });
      qc.invalidateQueries({ queryKey: ["recently_considered", uid] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const loading = loadingJ || loadingC || loadingA;

  const allItems: EditItem[] = [
    ...(jewelryEdit ?? []).map((i) => ({
      id: i.id,
      name: i.name,
      photo_url: i.photo_url,
      kind: "jewelry" as const,
      created_at: i.created_at ?? "",
      eye_verdict: (i as { eye_verdict?: string | null }).eye_verdict ?? null,
      ...bgFields(i),
    })),
    ...(clothingEdit ?? []).map((i) => ({
      id: i.id,
      name: i.name,
      photo_url: i.photo_url,
      kind: "clothing" as const,
      created_at: i.created_at ?? "",
      eye_verdict: (i as { eye_verdict?: string | null }).eye_verdict ?? null,
      ...bgFields(i),
    })),
    ...(accessoryEdit ?? []).map((i) => ({
      id: i.id,
      name: i.name,
      photo_url: i.photo_url,
      kind: "accessory" as const,
      created_at: i.created_at ?? "",
      eye_verdict: (i as { eye_verdict?: string | null }).eye_verdict ?? null,
      ...bgFields(i),
    })),
  ].sort((a, b) => b.created_at.localeCompare(a.created_at));

  const displayed =
    activeTab === "all"
      ? allItems
      : allItems.filter((i) => i.kind === activeTab);

  const linkTo = (item: EditItem) =>
    item.kind === "clothing"
      ? "/clothing/$id"
      : item.kind === "accessory"
      ? "/accessories/$id"
      : "/jewelry/$id";

  const promote = (item: EditItem) => {
    if (item.kind === "clothing") promoteClothing.mutate(item.id);
    else if (item.kind === "accessory") promoteAccessory.mutate(item.id);
    else promoteJewelry.mutate(item.id);
  };

  const totalCount = allItems.length;
  const tabs: { key: typeof activeTab; label: string }[] = [
    { key: "all", label: "All" },
    { key: "jewelry", label: "Jewelry" },
    { key: "clothing", label: "Clothing" },
    { key: "accessory", label: "Accessories" },
  ];

  return (
    <div className="max-w-4xl mx-auto px-8 py-8 pb-32">
      <div className="flex items-center justify-between mb-8">
        <button
          type="button"
          onClick={() => navigate({ to: "/" })}
          className="inline-flex items-center text-[11px] uppercase tracking-[0.25em] text-[var(--caileve-lilac-text)] hover:text-[var(--caileve-ink)]"
        >
          <ChevronLeft className="h-3 w-3 mr-1" /> Home
        </button>
      </div>

      <header className="mb-10">
        <p className="text-[10px] uppercase tracking-[0.2em] text-[var(--caileve-lilac-text)]">
          Your Edit
        </p>
        <h1
          className="font-serif-display text-4xl mt-2 leading-tight"
          style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", color: "var(--caileve-ink)" }}
        >
          Pieces that caught your eye.
        </h1>
        <p
          className="mt-3 text-base"
          style={{
            fontFamily: "'Cormorant Garamond', Georgia, serif",
            fontStyle: "italic",
            color: "var(--caileve-muted)",
          }}
        >
          {totalCount === 0
            ? "Nothing here yet."
            : totalCount === 1
            ? "One piece you are thinking about."
            : `${totalCount} pieces you are thinking about.`}
        </p>
      </header>

      {totalCount > 0 && (
        <div className="flex gap-2 mb-8 flex-wrap">
          {tabs.map((t) => {
            const count =
              t.key === "all"
                ? allItems.length
                : allItems.filter((i) => i.kind === t.key).length;
            if (t.key !== "all" && count === 0) return null;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => setActiveTab(t.key)}
                style={{
                  padding: "6px 16px",
                  borderRadius: "var(--caileve-radius-pill)",
                  fontSize: 12,
                  letterSpacing: "0.1em",
                  textTransform: "uppercase",
                  border: `1px solid ${activeTab === t.key ? "var(--caileve-gold)" : "var(--caileve-soft)"}`,
                  backgroundColor: activeTab === t.key ? "var(--caileve-lilac)" : "transparent",
                  color: activeTab === t.key ? "var(--caileve-bg)" : "var(--caileve-ink)",
                  cursor: "pointer",
                  transition: "all var(--caileve-transition-fast)",
                }}
              >
                {t.label}{count > 0 ? ` (${count})` : ""}
              </button>
            );
          })}
        </div>
      )}

      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <div key={i}>
              <div
                className="animate-pulse w-full"
                style={{ aspectRatio: "1 / 1", backgroundColor: "var(--caileve-soft)" }}
              />
              <div
                className="animate-pulse mt-3"
                style={{ height: 16, width: "75%", backgroundColor: "var(--caileve-soft)" }}
              />
            </div>
          ))}
        </div>
      ) : displayed.length === 0 ? (
        <EmptyState
          icon={Eye}
          message="Your edit is empty."
          subMessage="When a piece catches your eye, save it from Cailève Eye. It will wait here until you decide."
        />
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
          {displayed.map((item) => {
            const resolved = resolvePreferredPhoto(item);
            return (
            <div key={`${item.kind}-${item.id}`}>
              <Link
                to={linkTo(item)}
                params={{ id: item.id }}
                className="block"
              >
                <ImageBox src={resolved.src} alt={item.name} style={resolved.backdropStyle} />
                <p
                  className="font-serif-display text-lg mt-3 leading-tight"
                  style={{ color: "var(--caileve-ink)" }}
                >
                  {cleanItemName(item.name)}
                </p>
                {item.eye_verdict && (
                  <p
                    className="font-serif-display mt-1 leading-tight line-clamp-2"
                    style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontStyle: "italic", color: "var(--caileve-muted)", fontSize: 13 }}
                  >
                    {item.eye_verdict}
                  </p>
                )}
              </Link>
              <button
                type="button"
                onClick={() => promote(item)}
                style={{
                  fontSize: 11,
                  color: "var(--caileve-lilac)",
                  textDecoration: "underline",
                  background: "none",
                  border: "none",
                  padding: "6px 0 0",
                  cursor: "pointer",
                  textTransform: "uppercase",
                  letterSpacing: "0.1em",
                }}
              >
                This one is mine.
              </button>
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
